import * as React from 'react';
import styled from '@emotion/styled';
import { hasHotelServiceCharges } from '../../helpers/hotelServiceChargesHelper';
import { convertToDualCurrency, convertToEur } from '../../helpers/priceHelpers';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, IFooterLinkWithIcon, IRate } from '../../interfaces';
import { IExchangeRate } from '../../interfaces/IHeader';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { AttentionIcon } from '../Icons/AttentionIcon';
import { EnvelopeIcon } from '../Icons/EnvelopeIcon';
import { PhoneIcon } from '../Icons/PhoneIcon';
import { getCurrencyString } from '../PriceString';
import { CalamityFundChargeText } from '../CalamityFundText';
import { RootStateOrAny, useSelector } from 'react-redux';
import { RequestFormSoldOut } from './RequestFormSoldOut';
import { checkDirectBookingAvailability } from '../../helpers/directBookingMarketSupported';
import { IFeatureFlag } from '../../interfaces/IFeatureFlag';
import { IBrandConfig } from '../../reducers/pageData';

interface IRequestFormPrice {
    cabin: ICabinInfo;
    departure: number;
    isDualCurrency: boolean;
    exchangeRate: IExchangeRate;
    discountThreshold: number;
    countryCode: string;
    company: string;
    ship: string;
    showDirectBookingLabel: boolean;
    phone: IFooterLinkWithIcon;
    email: IFooterLinkWithIcon;
    searchUrlSlug: string;
    selectedAirportCode?: string;
    selectedFlightCode?: string;
    isAllInclusive?: boolean;
    selectedRates?: IRate[];
    market?: string;
    featureFlags: IFeatureFlag[];
    brandConfig: IBrandConfig;
}

const ActionButtonStyled = styled.button`
    color: ${({ theme }) => theme.button.primary.text};
    outline: none;
    position: relative;
    display: block;
    font-size: 16px;
    font-weight: 700;
    text-decoration: none;
    max-width: 300px;
    width: 100%;
    border: 0;
    margin: auto;
    padding: 13px 30px 11px 20px;
    background: ${({ theme }) => theme.button.primary.background};
    cursor: pointer;
    &:hover {
        color: ${({ theme }) => theme.button.primary.textHover};
        background: ${({ theme }) => theme.button.primary.backgroundHover};
        svg {
            fill: ${({ theme }) => theme.button.primary.iconHover};
        }
    }
    svg {
        fill: ${({ theme }) => theme.button.primary.icon};
        top: 50%;
        right: 7px;
        position: absolute;
        transform: translateY(-50%);
    }
`;

const LinkStyled = styled.a`
    color: ${({ theme }) => theme.link.primary};
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
    }
`;

export const RequestFormPrice = (props: IRequestFormPrice) => {
    const {
        cabin,
        departure,
        isDualCurrency,
        exchangeRate,
        discountThreshold,
        countryCode,
        company,
        ship,
        showDirectBookingLabel,
        phone,
        email,
        searchUrlSlug,
        selectedAirportCode,
        isAllInclusive,
        selectedRates,
        market,
        selectedFlightCode,
    } = props;
    const isMarketSupportsBooking = checkDirectBookingAvailability({
        featureFlags: props.featureFlags,
        brandConfig: props.brandConfig,
    });

    const [isNewRequestFormVariation, setIsNewRequestFormVariation] = React.useState(
        isMarketSupportsBooking,
    );

    const hasDiscount = cabin
        ? discountThreshold
            ? cabin.discountPercentage >= discountThreshold
            : cabin.discountPercentage > 0
        : false;

    const hasHSC = hasHotelServiceCharges(countryCode, company, ship, departure);

    let perPersonPrice = cabin?.perPersonPrice;
    let withFlightPrice = 0;
    const selectedRate = selectedRates?.find(
        ({ cabintype_id }) => cabintype_id === cabin.cabinType.nid,
    );
    const ratePrice = isAllInclusive ? selectedRate?.all_in_price : selectedRate?.cheapest_price;

    if (selectedAirportCode && cabin) {
        const flight = ratePrice?.flights?.find(
            ({
                departure_airport_code,
                departure_flight_code,
                return_airport_code,
                return_flight_code,
                departure_class_code,
                return_class_code,
            }) => {
                const isAirportMatched =
                    departure_airport_code === selectedAirportCode ||
                    departure_flight_code?.split(' ')[0] === selectedAirportCode ||
                    return_airport_code === selectedAirportCode ||
                    return_flight_code?.split(' ')[0] === selectedAirportCode;
                const isFlightClassMatched = selectedFlightCode === departure_class_code;

                return selectedFlightCode
                    ? isFlightClassMatched && isAirportMatched
                    : isAirportMatched;
            },
        );
        withFlightPrice = flight?.per_person_price;
        withFlightPrice = !isDualCurrency
            ? withFlightPrice
            : convertToEur(exchangeRate, withFlightPrice);

        perPersonPrice = withFlightPrice || cabin.perPersonPrice;
    } else {
        perPersonPrice = ratePrice?.per_person_price;
    }
    const isEstimatedPrice = ratePrice?.is_estimated_price;

    let isPriceValid = cabin?.cabinType?.price?.valid;
    const isPriceOnRequest =
        cabin && cabin.cabinType?.price?.string === __('on request', 'dreamlines');

    if (perPersonPrice === null) {
        isPriceValid = false;
    }

    const isSoldOut = !isPriceValid && !isPriceOnRequest && !!cabin;
    const renderPriceNumber = () => {
        if (isDualCurrency && !!exchangeRate) {
            return (
                <div>
                    {getCurrencyString(
                        convertToDualCurrency(exchangeRate, perPersonPrice),
                        exchangeRate?.sourceCurrency,
                    )}
                    {` ${__('p.P.', 'dreamlines')}`}
                    <span className="dual_currency">
                        {getCurrencyString(perPersonPrice, cabin.currency)}
                        {` ${__('p.P.', 'dreamlines')}`}
                    </span>
                </div>
            );
        }
        return (
            <div>
                {getCurrencyString(perPersonPrice, cabin.currency)}
                {` ${__('p.P.', 'dreamlines')}`}
            </div>
        );
    };

    return (
        <div id="requestFormContainer__price">
            {isPriceValid && !isEstimatedPrice && (
                <div>
                    {hasDiscount && !withFlightPrice && (
                        <del id="requestFormContainer__price__discount">
                            {isDualCurrency && !!exchangeRate ? (
                                <div>
                                    {getCurrencyString(
                                        convertToDualCurrency(
                                            exchangeRate,
                                            ratePrice?.catalog_per_person_price,
                                        ),
                                        exchangeRate?.sourceCurrency,
                                    )}
                                    {` ${__('p.P.', 'dreamlines')} / `}
                                    {getCurrencyString(
                                        ratePrice?.catalog_per_person_price,
                                        ratePrice?.currency,
                                    )}
                                    {` ${__('p.P.', 'dreamlines')}`}
                                </div>
                            ) : (
                                <div>
                                    {getCurrencyString(
                                        ratePrice?.catalog_per_person_price,
                                        ratePrice?.currency,
                                    )}
                                    {` ${__('p.P.', 'dreamlines')}`}
                                </div>
                            )}
                        </del>
                    )}
                    <div
                        id="requestFormContainer__price__container"
                        data-testid="request-form-has-price"
                    >
                        {hasDiscount && !withFlightPrice && (
                            <div id="requestFormContainer__price__discount">
                                -{ratePrice?.discount_percentage}%
                            </div>
                        )}
                        <div
                            id="requestFormContainer__price__container__main-price"
                            className={`${hasDiscount && !withFlightPrice ? 'has_dicount' : ''}`}
                        >
                            {renderPriceNumber()}
                        </div>
                        <div
                            id="requestFormContainer__price__container__label"
                            dangerouslySetInnerHTML={{
                                __html: __('tripPricePerPerson', 'dreamlines'),
                            }}
                        ></div>
                    </div>
                    {hasHSC && (
                        <div id="requestFormContainer__price__container__hotel-service-charges">
                            <b>{__('incl. hotel service charges', 'dreamlines')}</b>
                        </div>
                    )}
                    {showDirectBookingLabel && ['de'].includes(countryCode) && (
                        <div id="requestFormContainer__price__container__guarantee-offer">
                            <b>{__('request.boldText', 'dreamlines')}</b>
                        </div>
                    )}
                    {market === 'dreamlines.nl' && <CalamityFundChargeText />}
                </div>
            )}
            {isEstimatedPrice && (
                <div className="requestFormContainer__price__on-request">
                    {__('priceOnRequest', 'dreamlines')}
                </div>
            )}
            {!isPriceValid && isPriceOnRequest && (
                <div id="requestFormContainer__price__container">
                    <div
                        id="requestFormContainer__price__container__main-price"
                        data-testid="onrequest-only-NL-and-RU"
                    >
                        {__('on request', 'dreamlines')}
                    </div>
                </div>
            )}
            {isSoldOut && !isNewRequestFormVariation && (
                <div id="requestFormContainer__price__soldout" data-testid="request-form-sold-out">
                    <div id="requestFormContainer__price__soldout-container">
                        <div id="requestFormContainer__price__soldout-container__icon">
                            <AttentionIcon width={44} height={44} hasFill={true} />
                        </div>
                        <h6 id="requestFormContainer__price__soldout-container__title">
                            <b>{__('request.soldOutSailMessage', 'dreamlines')}</b>
                        </h6>
                        <p
                            id="requestFormContainer__price__soldout-container__paragraph"
                            dangerouslySetInnerHTML={{
                                __html: __('request.soldOutSubText', 'dreamlines'),
                            }}
                        ></p>
                    </div>
                    <div id="requestFormContainer__price__soldout-contacts">
                        <div id="requestFormContainer__price__soldout-contacts__container">
                            <a
                                className="requestFormContainer__price__soldout-contacts__container__link"
                                href={phone?.targetUrl}
                            >
                                <div className="requestFormContainer__price__soldout-contacts__container__unit">
                                    <PhoneIcon width={35} height={35} hasFill={true} />
                                    <div className="requestFormContainer__price__soldout-contacts__container__contact">
                                        <h6 className="requestFormContainer__price__soldout-contacts__container__contact-title">
                                            <b>{__('request.soldOutPhoneText', 'dreamlines')}</b>
                                        </h6>
                                        <p className="requestFormContainer__price__soldout-contacts__container__contact-content">
                                            {phone?.title}
                                        </p>
                                    </div>
                                </div>
                            </a>
                            <a
                                className="requestFormContainer__price__soldout-contacts__container__link"
                                href={email?.targetUrl}
                            >
                                <div className="requestFormContainer__price__soldout-contacts__container__unit">
                                    <EnvelopeIcon width={35} height={35} hasFill={true} />
                                    <div className="requestFormContainer__price__soldout-contacts__container__contact">
                                        <h6 className="requestFormContainer__price__soldout-contacts__container__contact-title">
                                            <b>{email.linkTitle}</b>
                                        </h6>
                                        <p className="requestFormContainer__price__soldout-contacts__container__contact-content email">
                                            <LinkStyled
                                                href={`mailto:${__(
                                                    'info@dreamlines.de',
                                                    'dreamlines',
                                                )}`}
                                            >
                                                {__('info@dreamlines.de', 'dreamlines')}
                                            </LinkStyled>
                                        </p>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div id="requestFormContainer__price__soldout-actions">
                        <a href={`/${searchUrlSlug}`}>
                            <ActionButtonStyled>
                                {__('request.soldOutCTAText', 'dreamlines')}
                                <ArrowRightIcon width={15} height={25} />
                            </ActionButtonStyled>
                        </a>
                    </div>
                </div>
            )}
            {isSoldOut && isNewRequestFormVariation && <RequestFormSoldOut soldOut={isSoldOut} />}
        </div>
    );
};
